import { ColGroupDef, ColDef, GridOptions } from 'ag-grid-community'
import { IssuerOrganisationDetail } from 'api'
import { Locale, Translation } from 'locale'
import {
  createActionsRenderer,
  gridOptionsBase,
  groupingRowColDef,
  TypedValueGetterParams,
  UserNameRenderer,
  withAllContext
} from 'view/components/datagrid'
import { booleanFormatter, dateQuickFilterTextGetter, makeDateAndTimeFormatter } from 'view/shared/datagrid'
import { IssuerOrganisationsActionsButton } from './IssuerOrganisationsActionsButton'
import { IssuerOrganisationsGridColumnId } from './IssuerOrganisationsColumnNames'

export const IssuerOrganisationsColumnDefs: (ColGroupDef | ColDef)[] = [
  groupingRowColDef,
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: null,
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(IssuerOrganisationsActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ISSUER_ORG_ID,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.issuerOrganisationId,
    headerName: Locale.translate(Translation.TC_ISSUER_ORG_ID),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ISSUER_ORGANISATION_NAME,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.issuerName,
    headerName: Locale.translate(Translation.TC_ISSUER_ORG_NAME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ISSUER_LEI,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.issuerLei,
    headerName: Locale.translate(Translation.TC_ISSUER_LEI),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ULTIMATE_PARENT_ORG_ID,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) =>
      params.data?.issuerOrganisation?.ultimateParentIssuerOrganisationId,
    headerName: Locale.translate(Translation.TC_ULTIMATE_PARENT_ORG_ID),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ULTIMATE_PARENT_NAME,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.ultimateParentName,
    headerName: Locale.translate(Translation.TC_ULTIMATE_PARENT_NAME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ULTIMATE_PARENT_LEI,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.ultimateParentLEI,
    headerName: Locale.translate(Translation.TC_ULTIMATE_PARENT_LEI),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_ULTIMATE_PARENT_PERM_ID,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.ultimateParentPermId,
    headerName: Locale.translate(Translation.TC_ULTIMATE_PARENT_PERM_ID),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_TRADED_ON_US_EXCHANGE,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.tradedOnUsExchange,
    headerName: Locale.translate(Translation.TC_TRADED_ON_US_EXCHANGE),
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_SANCTIONED_SCREENED,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.sanctionScreened,
    headerName: Locale.translate(Translation.TC_SANCTIONED_SCREENED),
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_COMPLIANCE_SUSPENDED,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.complianceSuspended,
    headerName: Locale.translate(Translation.TC_COMPLIANCE_SUSPENDED),
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_LAST_UPDATED_DATE_TIME,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.lastUpdateAt,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE_AND_TIME),
    width: 150,
    filter: 'agDateColumnFilter',
    valueFormatter: makeDateAndTimeFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: IssuerOrganisationsGridColumnId.ISSUER_ORGANISATIONS_LAST_UPDATED_BY,
    valueGetter: (params: TypedValueGetterParams<IssuerOrganisationDetail>) => params.data?.issuerOrganisation?.lastUpdateBy,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    cellRenderer: UserNameRenderer,
    width: 150,
    filter: 'agSetColumnFilter'
  }
]

export const issuerOrganisationsGridOptions: GridOptions<IssuerOrganisationDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.issuerOrganisation.id
}
